import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronDown,
  Clock3,
  ExternalLink,
  FileText,
  Shield,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { getThemeColor } from '../../config/appConfig';
import { useUser } from '../../contexts/UserContext';
import { invalidateKycVerifiedCache } from '../../hooks/useKycVerified';
import apiService from '../../utils/apiService';
import {
  clearKycVerifyFailCount,
  getKycVerifyFailCount,
  kycCacheUserId,
  recordKycVerifyFailure,
} from '../../utils/kycVerifyFailCache';
import {
  KYC_MAX_FILE_MB,
  acceptedDocsHelp,
  docsForCategory as filterDocsForCategory,
  enrichDocumentsWithCategories,
  collectStatusDocuments,
  exampleImageForType,
  normalizeCategories,
  normalizeDocumentTypes,
  overallBanner,
  parseKycStatusPayload,
  stepUiState,
  formatDocStatusLabel,
  validateKycFile,
  validateKycSelfieFile,
  withDocumentDisplayLabels,
  withUserKycFlags,
} from './kycComplianceData';
import KycImageUploadField from './KycImageUploadField';
import KycSelfieCapture from './KycSelfieCapture';
import '../../styles/module-shared.css';
import './kyc.css';

const THEME = getThemeColor('primary');

const KYC_VERIFY_PROGRESS_STEPS = [
  { title: 'Uploading documents…', detail: 'Sending your ID and selfie securely.' },
  { title: 'Verifying your identity…', detail: 'Checking document details with our verification service.' },
  { title: 'Matching face with ID…', detail: 'Comparing your selfie with the photo on your document.' },
  { title: 'Finalizing verification…', detail: 'Almost done — please keep this page open.' },
];

const BadgeIcon = ({ kind }) => {
  if (kind === 'verified') return <CheckCircle2 className="w-3.5 h-3.5" />;
  return <Clock3 className="w-3.5 h-3.5" />;
};

const KycHub = () => {
  const { userData, updateUserData } = useUser();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [typesByCategory, setTypesByCategory] = useState({});
  const [typesLoading, setTypesLoading] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [verifyStepIndex, setVerifyStepIndex] = useState(0);

  const cacheUserId = kycCacheUserId(userData);
  const alreadyVerified = Boolean(
    userData?.isVerified ||
      userData?.verified ||
      userData?.isAccountVerified ||
      userData?.isIdentityVerified ||
      status?.isVerified
  );

  const loadPage = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [catRes, statusRes] = await Promise.all([
        apiService.getKycDocumentCategories(),
        apiService.getKycStatus(),
      ]);

      let cats = [];
      if (!catRes.success) {
        const err = String(catRes.error || '');
        setLoadError(
          /failed to fetch/i.test(err)
            ? 'Cannot reach the Affiliate API. Start BrokerAffiliateAPI (https://localhost:7278) and refresh.'
            : err || 'Failed to load KYC categories'
        );
        setCategories([]);
      } else {
        cats = normalizeCategories(catRes.data);
        if (!cats.length) {
          setLoadError('No KYC document categories found.');
        }
        setCategories(cats);
      }

      const typeMap = {};
      if (cats.length) {
        await Promise.all(
          cats.map(async (cat) => {
            try {
              const res = await apiService.getKycDocumentTypes(cat.id);
              if (res.success) {
                typeMap[cat.id] = normalizeDocumentTypes(res.data);
              }
            } catch {
              // ignore per-category type failures
            }
          })
        );
        setTypesByCategory((prev) => ({ ...prev, ...typeMap }));
      }

      if (statusRes.success) {
        const statusPayload =
          statusRes.data && typeof statusRes.data === 'object' ? statusRes.data : {};
        const collected = collectStatusDocuments(statusPayload, statusPayload.myDocuments);
        const enrichedDocs = enrichDocumentsWithCategories(collected, cats, typeMap);
        const parsed = parseKycStatusPayload(
          { ...statusPayload, documents: enrichedDocs },
          enrichedDocs
        );
        parsed.documents = enrichedDocs;

        if (!parsed.isVerified) {
          const identityFromCats = cats
            .filter((c) => c.kind === 'identity')
            .flatMap((c) => filterDocsForCategory(enrichedDocs, c));

          if (
            identityFromCats.length &&
            parsed.identityVerification !== 'verified' &&
            parsed.accountVerification !== 'verified' &&
            parsed.identityVerification !== 'rejected' &&
            parsed.accountVerification !== 'rejected' &&
            parsed.docsStatus !== 'rejected' &&
            !identityFromCats.some((d) => d.status === 'rejected')
          ) {
            parsed.accountVerificationReview = true;
          }
          if (identityFromCats.length || enrichedDocs.length) {
            parsed.notStarted = false;
          }
          if (parsed.accountVerificationReview) {
            parsed.inReviewOverall = true;
          }
        }

        setStatus(parsed);
        if (parsed.isVerified) {
          invalidateKycVerifiedCache();
          clearKycVerifyFailCount(cacheUserId);
          const perms = JSON.parse(localStorage.getItem('userPermissions') || '[]');
          updateUserData(
            withUserKycFlags({
              ...(userData || {}),
              isVerified: true,
              verified: true,
              isAccountVerified: true,
              isIdentityVerified: true,
            }),
            perms
          );
        }
      } else if (!catRes.success) {
        // keep load error
      } else {
        setStatus(parseKycStatusPayload({ documents: [] }));
      }
    } catch (err) {
      setLoadError(err.message || 'Failed to load verification status');
    } finally {
      setLoading(false);
    }
  }, [updateUserData, userData, cacheUserId]);

  useEffect(() => {
    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!uploading) {
      setVerifyStepIndex(0);
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setVerifyStepIndex(0);
    const timer = window.setInterval(() => {
      setVerifyStepIndex((i) => Math.min(i + 1, KYC_VERIFY_PROGRESS_STEPS.length - 1));
    }, 2800);
    return () => {
      window.clearInterval(timer);
      document.body.style.overflow = prev;
    };
  }, [uploading]);

  const banner = useMemo(() => overallBanner(status || { docsStatus: 'pending' }), [status]);

  const docsForCategory = useCallback(
    (step) => filterDocsForCategory(status?.documents || [], step, typesByCategory),
    [status, typesByCategory]
  );

  const resetForm = () => {
    setSelectedTypeId('');
    setFrontFile(null);
    setBackFile(null);
    setSelfieFile(null);
    setUploadError('');
  };

  const toggleStep = async (step) => {
    const docs = filterDocsForCategory(status?.documents || [], step, typesByCategory);
    const ui = stepUiState(step, status || {}, docs);
    if (!ui.canExpand) return;

    if (expandedId === step.id) {
      setExpandedId(null);
      resetForm();
      return;
    }

    setExpandedId(step.id);
    resetForm();

    if (typesByCategory[step.id]) return;

    setTypesLoading(true);
    try {
      const res = await apiService.getKycDocumentTypes(step.id);
      if (res.success) {
        setTypesByCategory((prev) => ({
          ...prev,
          [step.id]: normalizeDocumentTypes(res.data),
        }));
      } else {
        setUploadError(res.error || 'Failed to load document types');
      }
    } catch (err) {
      setUploadError(err.message || 'Failed to load document types');
    } finally {
      setTypesLoading(false);
    }
  };

  const activeStep = categories.find((c) => c.id === expandedId);
  const activeTypes = typesByCategory[expandedId] || [];
  const selectedType = activeTypes.find((t) => String(t.id) === String(selectedTypeId));
  const firstTypeId = activeTypes[0]?.id;

  useEffect(() => {
    if (selectedTypeId || firstTypeId == null) return undefined;
    setSelectedTypeId(String(firstTypeId));
  }, [selectedTypeId, firstTypeId]);

  const canSubmitIdentity = Boolean(
    selectedTypeId && frontFile && selfieFile && !uploading
  );

  const handleSubmit = async () => {
    if (!activeStep || !selectedTypeId) return;
    setUploadError('');

    if (!frontFile) {
      setUploadError('Please upload the front of your ID.');
      return;
    }
    if (!selfieFile) {
      setUploadError('Please upload or capture a selfie to continue.');
      return;
    }

    const frontErr = validateKycFile(frontFile);
    if (frontErr) {
      setUploadError(frontErr);
      return;
    }
    if (backFile) {
      const backErr = validateKycFile(backFile);
      if (backErr) {
        setUploadError(backErr);
        return;
      }
    }
    const selfieErr = validateKycSelfieFile(selfieFile);
    if (selfieErr) {
      setUploadError(selfieErr);
      return;
    }

    setUploading(true);
    try {
      const res = await apiService.uploadKycDocuments(selectedTypeId, [], {
        frontFile,
        backFile,
        selfieFile,
        priorVerificationFailures: getKycVerifyFailCount(cacheUserId),
      });
      invalidateKycVerifiedCache();

      if (!res.success) {
        setUploadError(res.verificationMessage || res.error || res.message || 'Upload failed');
        return;
      }

      setFrontFile(null);
      setBackFile(null);
      setSelfieFile(null);

      const apiMsg = res.verificationMessage || res.message || '';
      const perms = JSON.parse(localStorage.getItem('userPermissions') || '[]');

      if (res.autoVerified || res.verificationOutcome === 'approved') {
        clearKycVerifyFailCount(cacheUserId);
        updateUserData(
          withUserKycFlags({
            ...(userData || {}),
            isVerified: true,
            verified: true,
            isAccountVerified: true,
            isIdentityVerified: true,
          }),
          perms
        );
      } else if (res.verificationOutcome === 'rejected') {
        recordKycVerifyFailure(cacheUserId, res.failedAttemptNumber);
        setUploadError(apiMsg || 'KYC verification failed. Please try again.');
      } else if (res.verificationOutcome === 'pending' && res.failedAttemptNumber) {
        recordKycVerifyFailure(cacheUserId, res.failedAttemptNumber);
        setUploadError(
          apiMsg || 'KYC could not be auto-verified. Your documents are pending manual review.'
        );
      }

      await loadPage();
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const verifyStep =
    KYC_VERIFY_PROGRESS_STEPS[
      Math.min(verifyStepIndex, KYC_VERIFY_PROGRESS_STEPS.length - 1)
    ];
  const verifyProgressPct = Math.round(
    ((verifyStepIndex + 1) / KYC_VERIFY_PROGRESS_STEPS.length) * 100
  );

  if (!loading && alreadyVerified) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="kyc-page">
        <div className="kyc-inner kyc-inner--wide">
          <div className="kyc-card kyc-loader">
            <AppLoader color={THEME} />
            <p className="kyc-loader__text">Loading verification status</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="kyc-page">
      <div className="kyc-inner kyc-inner--wide">
        <ModulePageHeader
          icon={Shield}
          title="KYC / Compliance"
          subtitle="Complete verification to unlock all features"
          iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
        />

        <ModuleAlert message={loadError} />
        <ModuleAlert message={uploadError} />

        <section className={`kyc-status-card is-${banner.key}`}>
          <div className="kyc-status-card__left">
            <div className="kyc-status-card__icon">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h2>Verification Status</h2>
              <p>{banner.message}</p>
            </div>
          </div>
          <span className={`kyc-pill is-${banner.key}`}>
            <BadgeIcon kind={banner.key} />
            {banner.badge}
          </span>
        </section>

        <div className="kyc-steps">
          {categories.map((step) => {
            const docs = docsForCategory(step);
            const ui = stepUiState(step, status || {}, docs);
            const open = expandedId === step.id;
            return (
              <article key={step.id} className={`kyc-step ${open ? 'is-open' : ''} is-${ui.key}`}>
                <button
                  type="button"
                  className="kyc-step__head"
                  onClick={() => toggleStep(step)}
                  disabled={!ui.canExpand}
                  aria-expanded={open}
                >
                  <div className="kyc-step__title">
                    <span className="kyc-step__glyph">
                      <FileText className="w-4 h-4" />
                    </span>
                    <div>
                      <strong>{step.label}</strong>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                  <div className="kyc-step__meta">
                    <span className={`kyc-pill is-${ui.key}`}>
                      <BadgeIcon kind={ui.key} />
                      {ui.badge}
                    </span>
                    {ui.canExpand ? (
                      <ChevronDown className={`kyc-step__chev ${open ? 'is-open' : ''}`} />
                    ) : null}
                  </div>
                </button>

                {open ? (
                  <div className="kyc-step__body">
                    {docs.length > 0 ? (
                      <>
                        {!ui.canUpload ? (
                          <p className="kyc-hint">
                            Upload is disabled while this category is under review or already verified.
                          </p>
                        ) : null}
                        <ul className="kyc-doc-list">
                          {withDocumentDisplayLabels(docs, step.kind).map((doc) => (
                            <li
                              key={
                                doc.userDocumentId ||
                                `${doc.fileName}-${doc.documentTypeId}-${doc.sideLabel}`
                              }
                            >
                              <div>
                                <strong>{doc.displayLabel}</strong>
                                <span className={`kyc-pill is-${doc.status}`}>
                                  {formatDocStatusLabel(doc.status, doc.statusRaw)}
                                </span>
                              </div>
                              {doc.url ? (
                                <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                  View file <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              ) : (
                                <span className="kyc-muted">{doc.fileName || '—'}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : null}

                    {ui.canUpload ? (
                      <div className="kyc-upload-form">
                        <label className="kyc-label" htmlFor={`kyc-type-${step.id}`}>
                          Document type
                        </label>
                        <select
                          id={`kyc-type-${step.id}`}
                          className="kyc-select"
                          value={selectedTypeId}
                          disabled={typesLoading || uploading}
                          onChange={(e) => setSelectedTypeId(e.target.value)}
                        >
                          <option value="">
                            {typesLoading ? 'Loading types…' : 'Select document type'}
                          </option>
                          {activeTypes.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.label}
                            </option>
                          ))}
                        </select>

                        <p className="kyc-hint">
                          Government-issued ID with photo. Upload a clear front (and back if available), then a matching selfie.
                        </p>
                        <p className="kyc-hint kyc-hint--soft">
                          PDF, JPG, or PNG (max {KYC_MAX_FILE_MB} MB). Selfie must be JPG or PNG.
                        </p>

                        {selectedType && exampleImageForType(selectedType.label) ? (
                          <div className="kyc-example">
                            <img
                              src={exampleImageForType(selectedType.label)}
                              alt={`Example for ${selectedType.label}`}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </div>
                        ) : null}

                        <div className="kyc-file-grid">
                          <KycImageUploadField
                            label="Front"
                            hint={`Upload front side of your ID, then crop so the full card is clear. PDF, JPG, or PNG (max ${KYC_MAX_FILE_MB} MB).`}
                            value={frontFile}
                            onChange={setFrontFile}
                            disabled={uploading}
                            cropTitle="Crop front of ID"
                            onError={setUploadError}
                          />
                          <KycImageUploadField
                            label="Back"
                            hint={`Upload back side of your ID, then crop so the full card is clear. PDF, JPG, or PNG (max ${KYC_MAX_FILE_MB} MB).`}
                            value={backFile}
                            onChange={setBackFile}
                            disabled={uploading}
                            cropTitle="Crop back of ID"
                            onError={setUploadError}
                          />
                        </div>

                        <KycSelfieCapture
                          value={selfieFile}
                          onChange={setSelfieFile}
                          disabled={uploading}
                          fileHint="JPG or PNG only."
                          onError={setUploadError}
                        />

                        <div className="kyc-form-actions">
                          <button
                            type="button"
                            className="kyc-btn kyc-btn--ghost"
                            disabled={uploading}
                            onClick={() => {
                              setExpandedId(null);
                              resetForm();
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="kyc-btn kyc-btn--primary"
                            disabled={!canSubmitIdentity}
                            onClick={handleSubmit}
                          >
                            {uploading ? 'Verifying...' : 'Submit'}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>

        <section className="kyc-card kyc-accepted">
          <h3>Accepted Documents</h3>
          <ul>
            {categories.map((step) => (
              <li key={`help-${step.id}`}>
                <strong>{step.label}</strong>
                <span>{acceptedDocsHelp(step)}</span>
              </li>
            ))}
            {!categories.length ? (
              <li>
                <strong>Documents</strong>
                <span>Upload a clear ID and matching selfie for verification.</span>
              </li>
            ) : null}
          </ul>
          <p className="kyc-accepted__foot">
            Need help? <Link to="/support">Contact support</Link>
          </p>
        </section>
      </div>

      {uploading ? (
        <div className="kyc-submit-overlay" aria-busy="true" aria-live="polite">
          <div className="kyc-submit-overlay-inner kyc-submit-overlay-inner--verify">
            <AppLoader color={THEME} />
            <p className="kyc-submit-overlay-title">{verifyStep.title}</p>
            <p className="kyc-submit-overlay-hint">{verifyStep.detail}</p>
            <div
              className="kyc-verify-progress"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={verifyProgressPct}
              aria-label="Verification progress"
            >
              <div className="kyc-verify-progress-bar" style={{ width: `${verifyProgressPct}%` }} />
            </div>
            <p className="kyc-verify-progress-label">{verifyProgressPct}%</p>
            <ul className="kyc-verify-steps" aria-hidden="true">
              {KYC_VERIFY_PROGRESS_STEPS.map((step, idx) => (
                <li
                  key={step.title}
                  className={
                    idx < verifyStepIndex ? 'is-done' : idx === verifyStepIndex ? 'is-active' : ''
                  }
                >
                  {step.title.replace('…', '')}
                </li>
              ))}
            </ul>
            <p className="kyc-submit-overlay-warning">
              Please wait — do not close or refresh this page.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default KycHub;
